// src/components/CrearNotificacionComponent.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./CrearNotificacionComponent.css";
import { Notification } from "../NotificationLoginComponent";
import { GetEventById, GetAllPolls, AddEvent, UpdateEvent, SendPushNotification, GetAllAccounts, GetAllPublicidad } from "../../Backend/ApiCalls";
import { BottomNav } from "../BottomNav";
import { FaArrowLeft } from "react-icons/fa";
import { useInAppNotifications } from "../../Contexts/InAppNotificationContext";
import { publicationNotificationRoute } from '../../utils/notificationLinks';
import { getActiveProfileToken } from "../../utils/profileStorage";

const API_BASE = "https://api.fallaplazarodrigo.es:999";
const LOCAL_NOTIFICATION_DELAY_MS = 2500;

export const CrearNotificacionComponent = ({ I18n, user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { showNotification } = useInAppNotifications();

  // — estados básicos —
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [recipients, setRecipients] = useState({
    todos: false,
    adultos: false,
    juveniles: false,
    infantiles: false,
  });
  // — Envío a personas concretas (por external_id de OneSignal) —
  const [personaMode, setPersonaMode] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [surveyLinked, setSurveyLinked] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState("");
  const [polls, setPolls] = useState([]);
  const [publicationLinked, setPublicationLinked] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState("");
  const [publications, setPublications] = useState([]);
  const [existingImages, setExistingImages] = useState([]); // URLs ya guardadas
  const [newImages, setNewImages] = useState([]); // { file, preview }
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState("");
  const [showMsg, setShowMsg] = useState(false);
  const [isError, setIsError] = useState(false);

  // —— envío de push a través del backend ——
  // La REST API Key de OneSignal ya NO está aquí: vive en el .env del
  // servidor y el endpoint /Notifications/SendPush (solo admins) hace el envío.
  const SendNotification = async (notificationPayload) => {
    const { response, data } = await SendPushNotification({
      title: notificationPayload.title,
      body: notificationPayload.body,
      route: publicationLinked && selectedPublication
        ? publicationNotificationRoute(selectedPublication)
        : "/notificaciones",
      externalIds: notificationPayload.externalIds,
      publicationId: publicationLinked && selectedPublication ? Number(selectedPublication) : null,
    });
    if (!response.ok) {
      throw new Error(data?.Error || "Error enviando la notificación push");
    }
    return data;
  };

  // helper: solo envía push cuando es NUEVA notificación (no edición).
  // En modo persona, envía SOLO a los usuarios seleccionados (external_id).
  // Si OneSignal no encuentra ningún dispositivo vinculado a esas personas
  // (recipients = 0), avisamos al admin en vez de fallar en silencio.
  const sendPushIfNew = async () => {
    if (isEditMode) return;
    try {
      const result = await SendNotification({
        title,
        body: description,
        externalIds: personaMode && selectedUserIds.length
          ? selectedUserIds.map(String)
          : undefined,
      });

      if (personaMode) {
        const recipientsCount = Number(result?.recipients ?? 0);
        const hasErrors =
          result?.errors &&
          (Array.isArray(result.errors) ? result.errors.length > 0 : true);

        if (recipientsCount === 0 || hasErrors) {
          console.warn("OneSignal sin destinatarios:", result);
          setMsg(
            "Notificación creada, pero el push no llegó a ningún dispositivo: " +
            "esa persona aún no tiene la app vinculada (debe abrir la versión " +
            "actualizada de la app al menos una vez)."
          );
          setIsError(true);
        }
      }
    } catch (err) {
      console.error("Error enviando push:", err);
      setMsg(`La notificación se creó, pero falló el envío del push: ${err.message}`);
      setIsError(true);
    }
  };

  const getToken = () => getActiveProfileToken();

  const showCreatedNotificationBubble = () => {
    if (isEditMode) return;

    const localId = `local-created-${Date.now()}`;

    window.setTimeout(() => {
      showNotification({
        id: localId,
        notificationId: localId,
        title,
        message: description,
        route: publicationLinked && selectedPublication
          ? publicationNotificationRoute(selectedPublication)
          : "/notificaciones",
        additionalData: publicationLinked && selectedPublication
          ? { type: 'publication', publicationId: Number(selectedPublication) }
          : {},
        source: "local-create"
      });
    }, LOCAL_NOTIFICATION_DELAY_MS);
  };

  // — Carga encuestas —
  useEffect(() => {
    (async () => {
      const { response, data } = await GetAllPolls();
      if (response.ok) setPolls(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { response, data } = await GetAllPublicidad();
        if (response.ok && Array.isArray(data)) setPublications(data);
      } catch (error) {
        console.error('Error cargando publicaciones:', error);
      }
    })();
  }, []);

  // — Carga usuarios al activar "A una persona" (solo la primera vez) —
  useEffect(() => {
    if (!personaMode || usersLoaded) return;
    (async () => {
      try {
        const { response, data } = await GetAllAccounts();
        if (response.ok && Array.isArray(data)) {
          const sorted = [...data].sort((a, b) =>
            `${a.surname || ""} ${a.name || ""}`.localeCompare(
              `${b.surname || ""} ${b.name || ""}`,
              "es"
            )
          );
          setAllUsers(sorted);
        }
      } catch (err) {
        console.error("Error cargando usuarios:", err);
      } finally {
        setUsersLoaded(true);
      }
    })();
  }, [personaMode, usersLoaded]);

  const togglePersonaMode = (checked) => {
    setPersonaMode(checked);
    if (checked) {
      // exclusivo con los grupos
      setRecipients({ todos: false, adultos: false, juveniles: false, infantiles: false });
    } else {
      setSelectedUserIds([]);
      setUserSearch("");
    }
  };

  const toggleUserSelected = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((u) => u !== userId) : [...prev, userId]
    );
  };

  const filteredUsers = allUsers.filter((u) => {
    if (!userSearch) return true;
    const text = `${u.name || ""} ${u.surname || ""} ${u.username || ""}`.toLowerCase();
    return text.includes(userSearch.toLowerCase());
  });

  // — Carga evento en edición —
  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      const { response, data } = await GetEventById(id);
      if (!response.ok) return;
      setTitle(data.title);
      setDescription(data.description);
      setLocation(data.location);
      setDate(data.fecha);
      setTime(data.hora);
      setSurveyLinked(data.surveyLinked);
      setSelectedSurvey(data.selectedSurvey || "");
      setPublicationLinked(Boolean(data.publicationId));
      setSelectedPublication(data.publicationId ? String(data.publicationId) : "");
      setRecipients({
        todos: data.recipients.includes("todos"),
        adultos: data.recipients.includes("adultos"),
        juveniles: data.recipients.includes("juveniles"),
        infantiles: data.recipients.includes("infantiles"),
      });
      // Destinatarios individuales guardados como "user:<Id>"
      const userRecipients = (data.recipients || [])
        .filter((r) => typeof r === "string" && r.startsWith("user:"))
        .map((r) => Number(r.slice(5)))
        .filter((n) => Number.isFinite(n));
      if (userRecipients.length) {
        setPersonaMode(true);
        setSelectedUserIds(userRecipients);
      }
      setExistingImages(Array.isArray(data.images) ? data.images : []);
    })();
  }, [id, isEditMode]);

  // — Handlers campos —
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      if (name === "surveyLinked") {
        setSurveyLinked(checked);
        if (!checked) setSelectedSurvey("");
        if (checked) {
          setPublicationLinked(false);
          setSelectedPublication("");
        }
      } else if (name === "publicationLinked") {
        setPublicationLinked(checked);
        if (!checked) setSelectedPublication("");
        if (checked) {
          setSurveyLinked(false);
          setSelectedSurvey("");
        }
      } else {
        setRecipients((r) => ({ ...r, [name]: checked }));
        // Los grupos son excluyentes con el modo "A una persona"
        if (checked && personaMode) {
          setPersonaMode(false);
          setSelectedUserIds([]);
          setUserSearch("");
        }
      }
    } else {
      switch (name) {
        case "title":
          setTitle(value);
          break;
        case "description":
          setDescription(value);
          break;
        case "location":
          setLocation(value);
          break;
        case "date":
          setDate(value);
          break;
        case "time":
          setTime(value);
          break;
        case "selectedSurvey":
          setSelectedSurvey(value);
          break;
        case "selectedPublication":
          setSelectedPublication(value);
          break;
        default:
          break;
      }
    }
  };

  // — Selector de archivos —
  const handleFileChange = (e) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
    }));
    setNewImages((prev) => [...prev, ...files]);
    e.target.value = null; // permite re-subir mismo archivo
  };
  const openFileDialog = () => fileInputRef.current?.click();

  // — Eliminar previsualizaciones —
  const removeExistingImage = (idx) => {
    setExistingImages((imgs) => imgs.filter((_, i) => i !== idx));
  };
  const removeNewImage = (idx) => {
    URL.revokeObjectURL(newImages[idx].preview);
    setNewImages((imgs) => imgs.filter((_, i) => i !== idx));
  };

  // — Validación —
  const validateForm = () => {
    const errs = {};
    if (!title) errs.title = I18n.ErrorTitleRequired || "El título es requerido.";
    if (!description)
      errs.description =
        I18n.ErrorDescriptionRequired || "La descripción es requerida.";
    if (!date) errs.date = I18n.ErrorDateRequired || "La fecha es requerida.";
    if (!time) errs.time = I18n.ErrorTimeRequired || "La hora es requerida.";
    if (personaMode && selectedUserIds.length === 0)
      errs.recipients = "Selecciona al menos una persona.";
    if (surveyLinked && !selectedSurvey)
      errs.linkedContent = "Selecciona una encuesta.";
    if (publicationLinked && !selectedPublication)
      errs.linkedContent = "Selecciona una publicación.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // — Envío del formulario —
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Modo persona → guardamos "user:<Id>" (la lista de notificaciones de la
    // app solo se la muestra a esos usuarios). Modo grupos → como siempre.
    const recArray = personaMode
      ? selectedUserIds.map((uid) => `user:${uid}`)
      : Object.keys(recipients).filter((k) => recipients[k]);

    // === CON NUEVAS IMÁGENES (FormData) ===
    if (newImages.length > 0) {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("location", location);
      formData.append("fecha", date);
      formData.append("hora", time);
      formData.append("recipients", JSON.stringify(recArray));
      formData.append("surveyLinked", surveyLinked ? "1" : "0");
      formData.append("selectedSurvey", surveyLinked ? selectedSurvey : "");
      formData.append("publicationId", publicationLinked ? selectedPublication : "");
      formData.append("existingImages", JSON.stringify(existingImages));
      newImages.forEach((img) => formData.append("newImages", img.file));

      try {
        const url = isEditMode
          ? `${API_BASE}/Events/UpdateEvent/${id}`
          : `${API_BASE}/Events/AddEvent`;

        const res = await fetch(url, {
          method: "POST",
          headers: { token: getToken() },
          body: formData,
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Error servidor");

        setMsg(
          isEditMode
            ? I18n.SuccessMessageEditNotification
            : I18n.SuccessMessageCreateNotification
        );
        setIsError(false);

        // enviar push al crear (también cuando hay imágenes)
        await sendPushIfNew();
        showCreatedNotificationBubble();

        if (!isEditMode) {
          setTitle("");
          setDescription("");
          setLocation("");
          setDate("");
          setTime("");
          setRecipients({
            todos: false,
            adultos: false,
            juveniles: false,
            infantiles: false,
          });
          setSurveyLinked(false);
          setSelectedSurvey("");
          setPublicationLinked(false);
          setSelectedPublication("");
          setPersonaMode(false);
          setSelectedUserIds([]);
          setUserSearch("");
          setExistingImages([]);
          setNewImages([]);
        }
      } catch (err) {
        console.error(err);
        setMsg(
          isEditMode
            ? I18n.ErrorMessageEditNotification
            : I18n.ErrorMessageCreateNotification
        );
        setIsError(true);
      }

      // === SIN NUEVAS IMÁGENES (JSON normal) ===
    } else {
      const payload = {
        title,
        description,
        location,
        fecha: date,
        hora: time,
        recipients: JSON.stringify(recArray),
        surveyLinked: surveyLinked ? 1 : 0,
        selectedSurvey: surveyLinked ? selectedSurvey : null,
        publicationId: publicationLinked ? Number(selectedPublication) : null,
        existingImages: JSON.stringify(existingImages),
      };

      try {
        const fn = isEditMode ? UpdateEvent(id, payload) : AddEvent(payload);
        const { response, data } = await fn;
        if (!response.ok) throw new Error(data.error || "Error servidor");

        setMsg(
          isEditMode
            ? I18n.SuccessMessageEditNotification
            : I18n.SuccessMessageCreateNotification
        );
        setIsError(false);

        await sendPushIfNew();
        showCreatedNotificationBubble();

        if (!isEditMode) {
          setTitle("");
          setDescription("");
          setLocation("");
          setDate("");
          setTime("");
          setRecipients({
            todos: false,
            adultos: false,
            juveniles: false,
            infantiles: false,
          });
          setSurveyLinked(false);
          setSelectedSurvey("");
          setPublicationLinked(false);
          setSelectedPublication("");
          setPersonaMode(false);
          setSelectedUserIds([]);
          setUserSearch("");
          setExistingImages([]);
        }
      } catch (err) {
        console.error(err);
        setMsg(
          isEditMode
            ? I18n.ErrorMessageEditNotification
            : I18n.ErrorMessageCreateNotification
        );
        setIsError(true);
      }
    }

    setShowMsg(true);
    setTimeout(() => setShowMsg(false), 5000);
  };

  // — Polls activas —
  const now = new Date();
  const activePolls = polls.filter((p) => {
    const d = new Date(p.date);
    const [h, m, s] = p.time.split(":");
    d.setHours(h, m, s || 0);
    return p.isPublished === 1 && d > now;
  });

  return (
    <div className="crear-notificacion">
      {showMsg && (
        <Notification message={msg} type={isError ? "error" : "success"} />
      )}

      <BottomNav I18n={I18n} user={user} />

      <div className="recipient">
        <h1 className="titulo-page">
          {isEditMode ? I18n.EditNotification : I18n.CreateNotification}
        </h1>
        <button
          className="back-button"
          onClick={() => navigate("/notificaciones")}
        >
          <FaArrowLeft className="back-icon" /> Volver
        </button>

        <div className="crear-notificacion-container">
          <form onSubmit={handleSubmit} className="create-notificarion-form">
            {/* Título */}
            <div>
              <label className="form-label">{I18n.Qualification}:</label>
              <input
                name="title"
                value={title}
                onChange={handleInputChange}
                className="create-notificarion-input-div"
              />
              {errors.title && (
                <span className="error-message">{errors.title}</span>
              )}
            </div>

            {/* Descripción */}
            <div>
              <label className="form-label">{I18n.Description}:</label>
              <textarea
                name="description"
                value={description}
                onChange={handleInputChange}
                className="create-notificarion-input-div"
              />
              {errors.description && (
                <span className="error-message">{errors.description}</span>
              )}
            </div>

            {/* Imágenes */}
            <div>
              <label className="form-label">Imágenes (opcional):</label>
              <div className="image-preview-container">
                {existingImages.map((url, i) => (
                  <div className="image-preview" key={"e" + i}>
                    <img src={url} alt="" />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeExistingImage(i)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {newImages.map((img, i) => (
                  <div className="image-preview" key={"n" + i}>
                    <img src={img.preview} alt="" />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeNewImage(i)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn-add-image"
                onClick={openFileDialog}
              >
                ＋
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>

            {/* Lugar */}
            <div>
              <label className="form-label">{I18n.Place}:</label>
              <input
                name="location"
                value={location}
                onChange={handleInputChange}
                className="create-notificarion-input-div"
              />
            </div>

            {/* Fecha / Hora */}
            <div>
              <label className="form-label">{I18n.Date}:</label>
              <input
                type="date"
                name="date"
                value={date}
                onChange={handleInputChange}
                className="create-notificarion-input-div"
              />
              {errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>
            <div>
              <label className="form-label">{I18n.Hour}:</label>
              <input
                type="time"
                name="time"
                value={time}
                onChange={handleInputChange}
                className="create-notificarion-input-div"
              />
              {errors.time && (
                <span className="error-message">{errors.time}</span>
              )}
            </div>

            {/* Destinatarios */}
            <div>
              <label className="form-label">{I18n.Recipients}:</label>
              <div className="checkbox-group">
                {[
                  { key: "todos", label: I18n.AllUsers },
                  { key: "adultos", label: I18n.Adults },
                  { key: "juveniles", label: I18n.Youth },
                  { key: "infantiles", label: I18n.Children },
                ].map((r) => (
                  <label key={r.key} className="checkbox-label">
                    <input
                      type="checkbox"
                      name={r.key}
                      checked={recipients[r.key]}
                      onChange={handleInputChange}
                    />{" "}
                    {r.label}
                  </label>
                ))}
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={personaMode}
                    onChange={(e) => togglePersonaMode(e.target.checked)}
                  />{" "}
                  A una persona
                </label>
              </div>

              {personaMode && (
                <div className="persona-selector">
                  <input
                    type="text"
                    className="create-notificarion-input-div persona-search"
                    placeholder="Buscar por nombre, apellidos o usuario..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />

                  {selectedUserIds.length > 0 && (
                    <p className="persona-selected-count">
                      {selectedUserIds.length} persona{selectedUserIds.length !== 1 ? "s" : ""} seleccionada{selectedUserIds.length !== 1 ? "s" : ""}
                    </p>
                  )}

                  <div className="persona-list">
                    {!usersLoaded && <p className="persona-empty">Cargando usuarios...</p>}
                    {usersLoaded && filteredUsers.length === 0 && (
                      <p className="persona-empty">Sin resultados.</p>
                    )}
                    {filteredUsers.map((u) => (
                      <label key={u.Id} className="persona-item">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(u.Id)}
                          onChange={() => toggleUserSelected(u.Id)}
                        />{" "}
                        {u.surname} {u.name}
                        {u.username ? (
                          <span className="persona-username"> · {u.username}</span>
                        ) : null}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {errors.recipients && (
                <span className="error-message">{errors.recipients}</span>
              )}
            </div>

            {/* Contenido vinculado */}
            <fieldset className="linked-content-panel">
              <legend>Contenido vinculado</legend>
              <div className="checkbox-group form-label">
              <label className="checkbox-label">
                {I18n.LinkedSurvey}
                <input
                  type="checkbox"
                  name="surveyLinked"
                  checked={surveyLinked}
                  onChange={handleInputChange}
                  style={{ marginLeft: 4 }}
                />
              </label>
              <label className="checkbox-label">
                Publicación vinculada
                <input
                  type="checkbox"
                  name="publicationLinked"
                  checked={publicationLinked}
                  onChange={handleInputChange}
                  style={{ marginLeft: 4 }}
                />
              </label>
              </div>
            {surveyLinked && (
              <div>
                <label className="form-label">{I18n.SelectASurvey}:</label>
                <select
                  name="selectedSurvey"
                  value={selectedSurvey}
                  onChange={handleInputChange}
                  className="create-survey-select"
                >
                  <option value="">{I18n.SelectSurvey}</option>
                  {activePolls.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {publicationLinked && (
              <div>
                <label className="form-label">Selecciona una publicación:</label>
                <select
                  name="selectedPublication"
                  value={selectedPublication}
                  onChange={handleInputChange}
                  className="create-survey-select"
                >
                  <option value="">Seleccionar publicación...</option>
                  {publications.map((publication) => (
                    <option key={publication.id} value={publication.id}>
                      {publication.title} · {publication.createdAt
                        ? new Date(publication.createdAt).toLocaleDateString('es-ES')
                        : 'Sin fecha'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {errors.linkedContent && <span className="error-message">{errors.linkedContent}</span>}
            </fieldset>

            {/* Enviar */}
            <button type="submit" className="btn-submit">
              {isEditMode ? I18n.Update : I18n.CreateNotification}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
